import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs } from '@/components/_common';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import PurchasesQuotationReportForm from '@/components/Reports/PurchasesReports/PurchasesQuotationReportForm';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useReportGenerate } from '@/hooks';
import { useQuotationReport } from '@/core/domain/questReport/Reports/Quotation/Quotation.hook';
import {
  QuestReportQuotationRequest,
  QuestResponseType,
} from '@/core/domain/questReport/Reports/Quotation/Quotation.types';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { QuestQuotationReportFormValues } from '@/core/domain/questReport/Reports/Quotation/Quotation.types';
import { DateTimeFormat } from '@/utils/moment';

function PurchasesQuotationPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<QuestQuotationReportFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useQuotationReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const {
        initialDate,
        finalDate,
        companiesIds,
        supplierIds,
        vehicleIds,
        ...values
      } = await form.validateFields();

      const obj: QuestReportQuotationRequest = {
        ...values,
        responseType:
          format === 'CSV' ? QuestResponseType.CSV : QuestResponseType.PDF,
        companiesIds: companiesIds?.toString(),
        supplierIds: supplierIds?.toString(),
        vehicleIds: vehicleIds?.toString(),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_cotacao.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_cotacao.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_cotacao.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <PurchasesQuotationReportForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        generateCsv={handleCsv}
        generatePdf={handlePdf}
        viewReport={handleView}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}

export default PurchasesQuotationPage;
